import { _isDev, _log } from '@shared/aux_helper_environment';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PrismaDynamicEnv } from 'core/services/ian-core-singleton.service';
import * as LZString from 'lz-string';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DashboardCard, DashboardGallery } from './ai-dashboard-storage.service';

/**
 * Interface for Dashboard Storage Providers
 * Defines the contract for storing and retrieving dashboard data
 * Can be implemented using localStorage, API, or other storage mechanisms
 */
export abstract class DashboardStorageProvider {
  /**
   * Comprime un string JSON usando LZ-String si la compresión está habilitada
   * @param jsonStr String JSON a comprimir
   * @param enableCompression Flag para habilitar compresión
   * @param useBase64 true para Base64 (Databricks), false para UTF16 (localStorage)
   * @returns String comprimido con prefijo o el string original
   */
  protected compressJson(jsonStr: string, enableCompression: boolean, useBase64: boolean = false): string {
    if (!enableCompression) {
      return jsonStr;
    }

    try {
      const compressed = useBase64 ? LZString.compressToBase64(jsonStr) : LZString.compress(jsonStr);
      // Agregar prefijo para identificar datos comprimidos
      const prefix = useBase64 ? 'LZ64:' : 'LZ:';
      return prefix + compressed;
    } catch (error) {
      console.warn('[StorageProvider] Compression failed, using uncompressed:', error);
      return jsonStr;
    }
  }

  /**
   * Descomprime un string JSON si está comprimido
   * Detecta automáticamente el tipo de compresión por el prefijo
   * @param data String potencialmente comprimido
   * @returns String JSON descomprimido
   */
  protected decompressJson(data: string | null): string | null {
    if (!data || typeof data !== 'string') {
      return data;
    }

    // Detectar compresión Base64 (Databricks)
    if (data.startsWith('LZ64:')) {
      try {
        const compressed = data.substring(5); // Remover prefijo "LZ64:"
        const decompressed = LZString.decompressFromBase64(compressed);
        if (!decompressed) {
          throw new Error('Decompression returned null');
        }
        return decompressed;
      } catch (error) {
        console.error('[StorageProvider] Base64 decompression failed:', error);
        return data;
      }
    }

    // Detectar compresión UTF16 (localStorage)
    if (data.startsWith('LZ:')) {
      try {
        const compressed = data.substring(3); // Remover prefijo "LZ:"
        const decompressed = LZString.decompress(compressed);
        if (!decompressed) {
          throw new Error('Decompression returned null');
        }
        return decompressed;
      } catch (error) {
        console.error('[StorageProvider] UTF16 decompression failed:', error);
        return data;
      }
    }

    // No está comprimido, retornar tal cual
    return data;
  }

  /**
   * Inicializa el usuario actual concatenando el código del ambiente
   * Esto asegura que las cards sean únicas por usuario y ambiente
   * @param prismaDynamicEnv Servicio para obtener configuración
   * @param oidcSecurityService Servicio de autenticación
   * @param logPrefix Prefijo para los logs (ej: '[ApiProvider]')
   * @returns Observable con el usuario completo (formato: email-envCode)
   */
  public initializeCurrentUser(
    prismaDynamicEnv: PrismaDynamicEnv,
    oidcSecurityService: OidcSecurityService,
    logPrefix: string = '[StorageProvider]'
  ): Observable<string | null> {
    // Obtener código del ambiente para hacer único el usuario por ambiente
    let envCode = _isDev ? 'DEV' : prismaDynamicEnv.getConf('brandCustomization.code', 'default');

    // Obtener usuario actual del servicio de autenticación
    return oidcSecurityService.getUserData().pipe(
      map((userData: any) => {
        // Usar email, userName, o sub como identificador del usuario
        const baseUser = userData?.email || userData?.preferred_username || null;

        // Concatenar código del ambiente al usuario para hacerlo único por ambiente
        const currentUser = baseUser ? `${baseUser}-${envCode}` : null;

        if (currentUser) {
          if (false) _log(`${logPrefix} Current user set with environment code:`, currentUser);
        }

        return currentUser;
      })
    );
  }

  /**
   * Save cards to storage
   * @param cards Array of dashboard cards to save
   * @returns Observable that completes when save is done
   */
  abstract saveCards(cards: DashboardCard[]): Observable<void>;

  /**
   * Load cards from storage
   * @returns Observable with array of dashboard cards
   */
  abstract loadCards(): Observable<DashboardCard[]>;

  /**
   * Clear all cards from storage
   * @returns Observable that completes when clear is done
   */
  abstract clearAllCards(): Observable<void>;

  /**
   * Save galleries to storage
   * @param galleries Array of dashboard galleries to save
   * @returns Observable that completes when save is done
   */
  abstract saveGalleries(galleries: DashboardGallery[]): Observable<void>;

  /**
   * Load galleries from storage
   * @returns Observable with array of dashboard galleries
   */
  abstract loadGalleries(): Observable<DashboardGallery[]>;
}
